<script setup lang="ts">
import { Ref, computed } from 'vue';
import { getDefaultLocaleValue } from '@/locales/default';
import type { ComponentPublicInstance } from 'vue';
import { Weights } from '@/types';

type TransmissionMode = 'manual' | 'automatic';

const props = defineProps<{
  translations: Record<string, string>;
  vehicle: any;
  validOptions: {
    weights: number[] | null;
    roofs: string[] | null;
    cabins: string[] | null;
    drivetrains: string[] | null;
    batteryCapacities: number[] | null;
    engines: string[] | null;
  };
  close: () => void;
  closeButtonRef?: Ref<HTMLElement | null>;
  transmissionMode: TransmissionMode | null;
}>();

const mode = computed(() => props.transmissionMode?.toLocaleLowerCase());

const setCloseButtonRef = (el: Element | ComponentPublicInstance | null) => {
  if (!props.closeButtonRef) return;
  props.closeButtonRef.value = el as HTMLElement | null;
};

const sizeTypeValue = computed(() => {
  const key =
    props.vehicle.sizeType === 'EXTRA_LONG'
      ? 'owvc_tech_data_sizeType_extraLong'
      : `owvc_tech_data_sizeType_${props.vehicle.sizeType.toLowerCase()}`;

  return props.translations[key];
});

const roofTypeValue = computed(() =>
  props.validOptions.roofs
    ? props.translations[
        `owvc_tech_data_roofType_${props.vehicle.roofHeightType.toLowerCase()}`
      ]
    : '',
);

const cabinsTypeValue = computed(() =>
  props.validOptions.cabins
    ? props.translations[
        `owvc_tech_data_cabinType_${props.vehicle.cabinType.toLowerCase()}`
      ]
    : '',
);

const driveTrainTypeValue = computed(() =>
  props.validOptions.drivetrains
    ? props.translations[
        `owvc_tech_data_driveTrain_${props.vehicle.drivetrainType.toLowerCase()}`
      ]
    : '',
);

const engineTypeValue = computed(() =>
  props.validOptions.engines
    ? props.translations[
        `owvc_tech_data_engineType_${props.vehicle.engineType.toLowerCase()}`
      ]
    : '',
);

const transmission = computed<keyof Weights | ''>(() => {
  const m = mode.value as keyof Weights | undefined;
  return m ?? '';
});

const weight = computed(() => {
  const m = mode.value as keyof Weights | undefined;
  if (!m) return '';

  const w = props.vehicle.weights?.[m]?.weightMax;
  if (!w) return '';

  return `${w.value ?? ''} ${w.unit ?? ''}`.trim();
});

const fullTitle = computed(() => {
  const parts = [sizeTypeValue.value];
  if (roofTypeValue.value.length > 1) {
    parts.push(roofTypeValue.value);
  }

  if (cabinsTypeValue.value.length > 1) {
    parts.push(cabinsTypeValue.value);
  }

  if (engineTypeValue.value.length > 1) {
    parts.push(engineTypeValue.value);
  }

  if (props.validOptions.weights) {
    parts.push(weight.value);
  }

  if (driveTrainTypeValue.value.length > 1) {
    parts.push(driveTrainTypeValue.value);
  }

  if (transmission.value.length > 1) {
    parts.push(
      props.translations[`owvc_tech_data_transmission_${transmission.value}`] ||
        transmission.value.charAt(0).toUpperCase() +
          transmission.value.slice(1),
    );
  }

  return `${
    props.translations['owvc_tech_data_technical_data'] ||
    getDefaultLocaleValue('technicalData')
  } (${parts.join(', ')})`;
});
</script>

<template>
  <header class="modal-header" data-sticky="true">
    <span class="wb-text-l-strong" style="display: block">
      {{ fullTitle }}
    </span>
    <button
      type="button"
      :ref="setCloseButtonRef"
      aria-label="Fechar modal"
      class="wbx-round-button wbx-round-button--small"
      data-test-id="owvans-tech-data-modal-close-button"
      @click="props.close"
    >
      <wb7-icon name="bds/close/24" />
    </button>
  </header>
</template>

<style lang="scss">
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 12px 14px var(--wb-spacing-s);
  border-bottom: 1px solid var(--wb-grey-85);
  background-color: var(--wb-data-table-background-main-color);
  color: var(--wb-text-headline-text-headline-color);

  &[data-sticky='true'] {
    position: sticky;
    top: 0;
    z-index: 10;
  }
}

.wbx-round-button:focus {
  outline: 2px solid var(--wb-primary-color);
  outline-offset: 2px;
}
</style>
